"use client";

import { motion } from "framer-motion";
import { TriangleAlert, Play, CircleCheckBig } from "lucide-react";
import { Button } from "@/components/ui/button";
import { isMaxIterations, getCancellationText } from "@/lib/tool-utils";
import { getViewModeChrome } from "./view-mode-chrome";

interface GenericCancellationProps {
  toolName: string;
  result?: any;
  onContinue?: () => void;
  isLastMessage?: boolean;
  isCompleted?: boolean;
  viewMode?: string;
}

export function GenericCancellation({
  toolName,
  result,
  onContinue,
  isLastMessage = false,
  isCompleted = false,
  viewMode,
}: GenericCancellationProps) {
  const chrome = getViewModeChrome(viewMode);
  // Check if this is max iterations (special case with continue button)
  const isMaxIterationsCase = isMaxIterations(result);
  
  // Get appropriate text for this tool
  const { title: defaultTitle, subtitle: defaultSubtitle } = getCancellationText(toolName);
  
  // Determine display content
  let title: string;
  let subtitle: string;
  let IconComponent: any;
  let iconColor: string;
  
  if (isMaxIterationsCase) {
    if (isCompleted) {
      title = "Agent Continued";
      subtitle = "Resuming execution...";
      IconComponent = CircleCheckBig;
      iconColor = "text-green-500";
    } else {
      title = "Agent Paused";
      subtitle = "Do you wish to continue?";
      IconComponent = Play;
      iconColor = "text-blue-500";
    }
  } else {
    title = defaultTitle;
    subtitle = defaultSubtitle;
    IconComponent = TriangleAlert;
    iconColor = "text-yellow-500";
  }

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.98 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.2, ease: "easeOut" }}
      className="w-full"
    >
      <div className={`rounded-md border ${chrome.card} overflow-hidden`}>
        <div className="px-3 py-2 flex items-center justify-between gap-3 min-w-0">
          <div className="flex items-center gap-2.5">
            {/* Tool icon placeholder - could be customized per tool */}
            <div className="w-7 h-7 rounded bg-muted/50 flex items-center justify-center">
              <div className="w-4 h-4 rounded bg-muted-foreground/20" />
            </div>

            {/* Text content */}
            <div className="flex flex-col">
              <span className={`text-[13px] font-semibold ${chrome.text} truncate`}>
                {title}
              </span>
              <span className={`text-[11px] ${chrome.subtext} truncate`}>
                {subtitle}
              </span>
            </div>
          </div>

          {/* Status icon on the right */}
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.1, type: "spring", stiffness: 300 }}
          >
            <IconComponent className={`h-4 w-4 ${iconColor}`} />
          </motion.div>
        </div>

        {/* Continue button for max iterations case - only show if last message and not completed */}
        {isMaxIterationsCase && onContinue && isLastMessage && !isCompleted && (
          <div className={`border-t ${chrome.divider} px-3 py-2`}>
            <Button
              onClick={onContinue}
              size="sm"
              className={`w-full ${chrome.button}`}
            >
              <Play className="w-4 h-4 mr-2" />
              Continue
            </Button>
          </div>
        )}
      </div>
    </motion.div>
  );
}
